import React, { useEffect, useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Box, Pagination, Paper, Typography, Dialog, DialogTitle, DialogActions,
    Chip, FormControl, InputLabel, Select, MenuItem, IconButton, DialogContent, TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { staffAuthHeaders, staffSignInNotice } from '../lib/staffAuthHeaders';

// NOTE: AdminPanelSettingsIcon is not imported, so it's defined as a placeholder
function AdminPanelSettingsIcon() {
    // Replace with a proper MUI icon if available (e.g., import SettingsIcon from '@mui/icons-material/Settings';)
    return <span style={{fontSize: '1.2rem'}}>🛡️</span>;
}


const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.admin.marsreel.com';

const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
    overflowX: 'auto',
});

const TableHeaderCell = styled(TableCell)({
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#12c099',
    backgroundColor: '#1c1c1c',
    padding: '16px',
});

const TableRowStyled = styled(TableRow)({
    '&:nth-of-type(even)': { backgroundColor: '#282828' },
    '&:hover': { backgroundColor: '#333' },
});

const TableBodyCell = styled(TableCell)({
    color: '#fff',
    fontSize: '0.9rem',
    padding: '16px',
});

const ActionContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: '8px',
});

const StyledButton = styled(Button)({
    minWidth: '90px',
    textTransform: 'none',
});

const PaginationBox = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
});

const ApplicantsContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
});

const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: '20px',
});

// Map status -> Chip props (Added 'denied', 'interview')
const STATUS_META = {
    pending:  { label: 'Pending',  color: 'warning' },
    approved: { label: 'Approved', color: 'success' },
    rejected: { label: 'Rejected', color: 'error'  },
    denied:   { label: 'Denied',   color: 'error'  },
    interview: {label: 'Interview', color: 'info' }
};

const normalizeStatus = (s) => (s || '').toLowerCase().trim();

const Applicants = () => {
    const [allApplicants, setAllApplicants] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedApplicantId, setSelectedApplicantId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const PAGE_SIZE = 10;
    const [overrideModalOpen, setOverrideModalOpen] = useState(false);
    const [applicantToOverride, setApplicantToOverride] = useState(null);
    const [overrideReason, setOverrideReason] = useState("");

    useEffect(() => {
        fetchApplicants();
    }, []); // Fetch all applicants only on initial component mount

    const fetchApplicants = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/applicants`, { headers: await staffAuthHeaders() });
            setAllApplicants(response.data.applicants);
        } catch (error) {
            console.error('Error fetching applicants:', error);
            const notice = staffSignInNotice(error);
            if (notice) alert(notice);
        }
    };

    // Updated filtering logic to treat 'denied' and 'rejected' as the same category
    const filteredApplicants = useMemo(() => {
        if (statusFilter === 'all') {
            return allApplicants;
        }
        if (statusFilter === 'denied') {
            return allApplicants.filter(applicant => {
                const status = normalizeStatus(applicant.status);
                return status === 'denied' || status === 'rejected';
            });
        }
        return allApplicants.filter(applicant => normalizeStatus(applicant.status) === statusFilter);
    }, [allApplicants, statusFilter]);

    const paginatedApplicants = useMemo(() => {
        const startIndex = (page - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return filteredApplicants.slice(startIndex, endIndex);
    }, [filteredApplicants, page]);

    useEffect(() => {
        setTotalPages(Math.ceil(filteredApplicants.length / PAGE_SIZE));
        if (page > 1) setPage(1); // Reset to the first page whenever the filter changes
    }, [filteredApplicants]);


    // Map button labels to schema values (using 'Deny' -> 'denied')
    const mapUiStatusToSchema = (ui) => {
        switch (ui) {
            case 'Approve':  return 'approved';
            case 'Deny':     return 'denied';
            case 'Interview': return 'interview'; // ✅ ADDED INTERVIEW MAPPING
            case 'Reset':    return 'pending';
            default: return 'pending';
        }
    };

    const handleAction = async (id, uiActionLabel) => {
        const status = mapUiStatusToSchema(uiActionLabel);
        try {
            await axios.put(`${API_BASE_URL}/api/applicants/${id}/update-status`, { status }, { headers: await staffAuthHeaders() });
            await fetchApplicants();
            alert(`Applicant status updated to ${status}`);
        } catch (error) {
            console.error(`Error updating status of applicant ${id}:`, error);
            alert(staffSignInNotice(error) || error?.response?.data?.message || 'Failed to update status');
        }
    };

    const openDeleteDialog = (id) => {
        setSelectedApplicantId(id);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setSelectedApplicantId(null);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/applicants/${selectedApplicantId}`, { headers: await staffAuthHeaders() });
            fetchApplicants();
            closeDeleteDialog();
        } catch (error) {
            console.error('Error deleting applicant:', error);
            const notice = staffSignInNotice(error);
            if (notice) alert(notice);
        }
    };

    const handleFilterChange = (event) => {
        setStatusFilter(event.target.value);
    };

    const handleOpenOverrideModal = (applicant) => {
        setApplicantToOverride(applicant);
        setOverrideModalOpen(true);
    };

    const handleCloseOverrideModal = () => {
        setApplicantToOverride(null);
        setOverrideModalOpen(false);
        setOverrideReason(""); // Reset reason on close
    };

    const handleConfirmOverride = async () => {
        if (!applicantToOverride || !overrideReason) {
            alert("Please provide a reason for the override.");
            return;
        }
        try {
            await axios.put(`${API_BASE_URL}/api/applicants/${applicantToOverride._id}/approve-override`, {
                reason: overrideReason
            }, { headers: await staffAuthHeaders() });
            await fetchApplicants(); // Refresh the list
            handleCloseOverrideModal();
        } catch (error) {
            console.error('Error confirming override:', error);
            alert(staffSignInNotice(error) || 'Failed to process override.');
        }
    };

    return (
        <ApplicantsContainer>
            <Title>Applicants</Title>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="status-filter-label" sx={{ color: '#fff' }}>Filter by Status</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Filter by Status"
                        onChange={handleFilterChange}
                        sx={{
                            color: '#fff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                            '.MuiSvgIcon-root': { color: '#12c099' },
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="denied">Denied</MenuItem>
                        <MenuItem value="interview">Interview</MenuItem>

                    </Select>
                </FormControl>
            </Box>

            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeaderCell>Name</TableHeaderCell>
                            <TableHeaderCell>Email</TableHeaderCell>
                            <TableHeaderCell>Phone</TableHeaderCell>
                            <TableHeaderCell>City, State, Zip</TableHeaderCell>
                            <TableHeaderCell>Role</TableHeaderCell>
                            <TableHeaderCell>Date Applied</TableHeaderCell>
                            <TableHeaderCell>Status</TableHeaderCell>
                            <TableHeaderCell>Actions</TableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedApplicants.map((applicant) => {
                            const currentStatus = normalizeStatus(applicant.status);
                            const meta = STATUS_META[currentStatus] || { label: applicant.status, color: 'default' };

                            const isBgCheckClear = applicant.backgroundCheckStatus === 'clear';
                            const isApproveDisabled = currentStatus === 'approved' || !isBgCheckClear;
                            const isInterviewDisabled = currentStatus === 'approved' || currentStatus === 'denied' || currentStatus === 'interview';


                            return (
                                <TableRowStyled key={applicant._id}>
                                    <TableBodyCell>{applicant.name}</TableBodyCell>
                                    <TableBodyCell>{applicant.email}</TableBodyCell>
                                    <TableBodyCell>{applicant.phone}</TableBodyCell>
                                    <TableBodyCell>{`${applicant.address?.city || ''}, ${applicant.address?.state || ''}, ${applicant.address?.zip || ''}`}</TableBodyCell>
                                    <TableBodyCell>{applicant.role}</TableBodyCell>
                                    <TableBodyCell>{new Date(applicant.dateApplied).toLocaleDateString()}</TableBodyCell>
                                    <TableBodyCell>
                                        <Chip
                                            label={meta.label}
                                            variant="filled"
                                            size="small"
                                            sx={(theme) => {
                                                const bg =
                                                    theme.palette[meta.color]?.main ??
                                                    theme.palette.grey[700]; // fallback
                                                return {
                                                    bgcolor: bg,
                                                    color: theme.palette.getContrastText(bg),
                                                };
                                            }}
                                        />

                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <ActionContainer>
                                            <StyledButton
                                                onClick={() => handleAction(applicant._id, 'Approve')}
                                                color="success"
                                                variant="contained"
                                                size="small"
                                                // Disable if already approved or BG check is not clear
                                                disabled={isApproveDisabled}
                                                // Helpful tooltip for admins
                                                title={
                                                    !isBgCheckClear
                                                        ? "Background check must be 'Clear' before approving."
                                                        : ""
                                                }
                                            >
                                                Approve
                                            </StyledButton>

                                            {/* ✅ NEW INTERVIEW BUTTON */}
                                            <StyledButton
                                                onClick={() => handleAction(applicant._id, 'Interview')}
                                                color="info"
                                                variant="contained"
                                                size="small"
                                                disabled={isInterviewDisabled}
                                            >
                                                Interview
                                            </StyledButton>


                                            {/* OVERRIDE ICON: Shows only when needed */}
                                            {!isBgCheckClear && currentStatus !== 'approved' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenOverrideModal(applicant)}
                                                    title="Admin Override Approval"
                                                    sx={{ color: '#ff9800' }}
                                                >
                                                    <AdminPanelSettingsIcon />
                                                </IconButton>
                                            )}

                                            <StyledButton
                                                onClick={() => handleAction(applicant._id, 'Deny')}
                                                color="error"
                                                variant="contained"
                                                size="small"
                                                disabled={currentStatus === 'denied' || currentStatus === 'rejected'}
                                            >
                                                Deny
                                            </StyledButton>
                                            <StyledButton
                                                component={Link}
                                                to={`/applicants/${applicant._id}`}
                                                color="secondary"
                                                variant="contained"
                                                size="small"
                                            >
                                                View
                                            </StyledButton>
                                            <StyledButton
                                                onClick={() => openDeleteDialog(applicant._id)}
                                                color="warning"
                                                variant="contained"
                                                size="small"
                                            >
                                                Delete
                                            </StyledButton>
                                        </ActionContainer>
                                    </TableBodyCell>
                                </TableRowStyled>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            <PaginationBox>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    sx={{
                        '& .MuiPaginationItem-root': { color: '#fff' },
                        '& .Mui-selected': { backgroundColor: '#12c099 !important' }
                    }}
                />
            </PaginationBox>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>Are you sure you want to delete this applicant?</DialogTitle>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">Cancel</Button>
                    <Button onClick={confirmDelete} color="secondary">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Override Approval Modal */}
            <Dialog open={overrideModalOpen} onClose={handleCloseOverrideModal}>
                <DialogTitle>Admin Approval Override</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        The background check for **{applicantToOverride?.name}** is not 'Clear'.
                    </Typography>
                    <Typography gutterBottom>
                        Please provide a mandatory reason for overriding this and manually approving the applicant.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for Override"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseOverrideModal}>Cancel</Button>
                    <Button
                        onClick={handleConfirmOverride}
                        color="success"
                        variant="contained"
                        disabled={!overrideReason}
                    >
                        Confirm Approval
                    </Button>
                </DialogActions>
            </Dialog>
        </ApplicantsContainer>
    );
};

export default Applicants;
